import { useState, useCallback, useEffect, useMemo } from 'react';
import { PopoverProps } from './Popover';
import { useCallbackRef } from '@trello/layer-manager';

interface UsePopoverArgs {
  initialScreen?: number;
}

interface UsePopoverResult<
  TElementTrigger extends HTMLElement,
  TElementTarget extends HTMLElement = HTMLElement
> {
  // Refs for consumers to set
  triggerRef: (node: TElementTrigger) => void;
  targetRef: (node: TElementTarget) => void;

  // Single screen
  show: () => void;
  hide: () => void;
  toggle: () => void;

  // Multi screen
  push: (screen: number) => void;
  pop: (depth?: number) => void;

  popoverProps: PopoverProps;
}

export const usePopover = <
  TElementTrigger extends HTMLElement,
  TElementTarget extends HTMLElement = HTMLElement
>({ initialScreen }: UsePopoverArgs = {}): UsePopoverResult<
  TElementTrigger,
  TElementTarget
> => {
  // Refs
  const [triggerElement, triggerRef] = useCallbackRef<TElementTrigger>();
  const [targetElement, targetRef] = useCallbackRef<TElementTarget>();

  // Single screen
  const [isVisible, setVisible] = useState(false);
  const show = useCallback(() => {
    setVisible(true);
  }, []);
  const hide = useCallback(() => {
    setVisible(false);
  }, []);
  const toggle = useCallback(() => {
    setVisible((prev) => !prev);
  }, []);

  // Multi screen
  const [screenStack, setScreenStack] = useState([initialScreen]);
  const currentScreen = screenStack[screenStack.length - 1];
  const push = useCallback((screenId: number) => {
    setScreenStack((prev) => [...prev, screenId]);
  }, []);
  const pop = useCallback(
    (depth = 1) => {
      if (depth > screenStack.length - 1) {
        console.warn(
          '`depth` argument provided to `pop` exceeded the size of the screen stack',
        );
        return;
      }
      setScreenStack((prev) => prev.slice(0, -depth));
    },
    [screenStack.length],
  );

  // Clear the stack when hidden
  useEffect(() => {
    if (!isVisible && screenStack.length > 1) {
      setScreenStack([initialScreen]);
    }
  }, [isVisible, screenStack, setScreenStack, initialScreen]);

  const popoverProps = useMemo<PopoverProps>(
    () => ({
      isVisible,
      onHide: (reason) => {
        // Uncomment this line to see why popovers are being hidden internally
        // console.log('dialog hidden internally, reason:', reason);
        hide();
      },
      onBack: () => pop(),
      hasBackButton: screenStack.length > 1,
      targetElement,
      triggerElement,
      currentScreen,
    }),
    [
      isVisible,
      hide,
      pop,
      screenStack,
      currentScreen,
      targetElement,
      triggerElement,
    ],
  );

  return {
    targetRef,
    triggerRef,

    show,
    hide,
    toggle,
    push,
    pop,

    // These props are 'internal' the popover hook, and are
    // expected to be spread onto the <Popover> component
    popoverProps,
  };
};
